/**
 * Exit Interview — Digital Form
 * Route: /exit-interview (add to App.js for ALL roles)
 * Also accessible from Offboarding page
 *
 * Backend routes needed:
 *   POST /offboarding/exit-interview          — employee submits form
 *   GET  /offboarding/exit-interview/:empId   — HR views a submission
 *   GET  /offboarding/exit-interviews         — HR views all submissions
 *
 * Add to offboarding.routes.js (wired in app.js under /api/v1/offboarding)
 */

import React, { useState, useEffect } from 'react';
import {
    Container, Card, Row, Col, Button,
    Form, Badge, Spinner, Alert, ProgressBar,
} from 'react-bootstrap';
import {
    LogOut, CheckCircle, Star, AlertCircle, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosConfig';

// ── Question sections ─────────────────────────────────────────────────────────
const SECTIONS = [
    {
        title: 'Reason for Leaving',
        icon: '🚪',
        questions: [
            {
                id: 'primary_reason',
                text: 'What is your primary reason for leaving?',
                type: 'mcq',
                required: true,
                options: [
                    'Better career opportunity',
                    'Higher compensation elsewhere',
                    'Work-life balance',
                    'Relocation',
                    'Personal/Family reasons',
                    'Health reasons',
                    'Pursuing higher education',
                    'Dissatisfied with management',
                    'Company culture/environment',
                    'Job role not as expected',
                    'Other',
                ],
            },
            {
                id: 'secondary_reason',
                text: 'Any secondary reason?',
                type: 'textarea',
                required: false,
                placeholder: 'Optional — share anything else that contributed',
            },
            {
                id: 'would_return',
                text: 'Would you consider returning to the company in the future?',
                type: 'rating3',
                required: true,
                options: ['Yes', 'Maybe', 'No'],
            },
        ],
    },
    {
        title: 'Job & Role Satisfaction',
        icon: '💼',
        questions: [
            {
                id: 'role_clarity',
                text: 'Was your role and responsibilities clearly defined?',
                type: 'nps',
                required: true,
                label_low: 'Not at all', label_high: 'Very clearly',
            },
            {
                id: 'workload',
                text: 'How was your workload?',
                type: 'mcq',
                required: true,
                options: ['Too heavy', 'Heavy but manageable', 'Just right', 'Light', 'Too light'],
            },
            {
                id: 'skills_used',
                text: 'Did your role make good use of your skills and abilities?',
                type: 'nps',
                required: true,
                label_low: 'Not at all', label_high: 'Fully utilized',
            },
            {
                id: 'job_satisfaction_comment',
                text: 'Any comments about your role?',
                type: 'textarea',
                required: false,
                placeholder: 'What did you love? What could have been better?',
            },
        ],
    },
    {
        title: 'Management & Leadership',
        icon: '👥',
        questions: [
            {
                id: 'manager_support',
                text: 'How would you rate your manager\'s support and guidance?',
                type: 'stars',
                required: true,
            },
            {
                id: 'manager_feedback',
                text: 'Did you receive regular and helpful feedback from your manager?',
                type: 'boolean',
                required: true,
            },
            {
                id: 'management_comment',
                text: 'Any feedback for the management team?',
                type: 'textarea',
                required: false,
                placeholder: 'What could managers do better?',
            },
        ],
    },
    {
        title: 'Company Culture & Environment',
        icon: '🏢',
        questions: [
            {
                id: 'culture_rating',
                text: 'How would you rate the overall company culture?',
                type: 'nps',
                required: true,
                label_low: 'Very poor', label_high: 'Excellent',
            },
            {
                id: 'team_collaboration',
                text: 'How well did your team collaborate?',
                type: 'nps',
                required: true,
                label_low: 'Poor', label_high: 'Excellent',
            },
            {
                id: 'dei_rating',
                text: 'Did you feel included and respected at work?',
                type: 'nps',
                required: true,
                label_low: 'Not at all', label_high: 'Absolutely',
            },
            {
                id: 'culture_comment',
                text: 'What would you change about the company culture?',
                type: 'textarea',
                required: false,
                placeholder: 'Be honest — this helps us improve',
            },
        ],
    },
    {
        title: 'Compensation & Benefits',
        icon: '💰',
        questions: [
            {
                id: 'comp_satisfaction',
                text: 'How satisfied were you with your compensation?',
                type: 'nps',
                required: true,
                label_low: 'Very unsatisfied', label_high: 'Very satisfied',
            },
            {
                id: 'benefits_satisfaction',
                text: 'How satisfied were you with the benefits package?',
                type: 'nps',
                required: true,
                label_low: 'Very unsatisfied', label_high: 'Very satisfied',
            },
        ],
    },
    {
        title: 'Final Thoughts',
        icon: '💡',
        questions: [
            {
                id: 'recommend_company',
                text: 'How likely are you to recommend this company to others?',
                type: 'nps',
                required: true,
                label_low: 'Not at all', label_high: 'Definitely',
            },
            {
                id: 'biggest_achievement',
                text: 'What was your biggest achievement during your time here?',
                type: 'textarea',
                required: false,
                placeholder: 'Tell us something you\'re proud of',
            },
            {
                id: 'suggestions',
                text: 'What suggestions do you have for improving the company?',
                type: 'textarea',
                required: false,
                placeholder: 'Your honest feedback is invaluable to us',
            },
            {
                id: 'overall_experience',
                text: 'Overall, how was your experience working here?',
                type: 'stars',
                required: true,
            },
        ],
    },
];

// ── Question renderers ────────────────────────────────────────────────────────
const NPSInput = ({ q, value, onChange }) => (
    <div>
        <div className="d-flex gap-1 flex-wrap">
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <button key={n} type="button"
                    onClick={() => onChange(n)}
                    style={{
                        width:40, height:40, borderRadius:8, cursor:'pointer',
                        border: value===n ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                        background: value===n ? '#4f46e5' : '#fff',
                        color: value===n ? '#fff' : '#374151',
                        fontWeight:600, fontSize:14, transition:'all 0.15s',
                    }}>
                    {n}
                </button>
            ))}
        </div>
        {(q.label_low || q.label_high) && (
            <div className="d-flex justify-content-between mt-1">
                <span style={{fontSize:11, color:'#94a3b8'}}>{q.label_low}</span>
                <span style={{fontSize:11, color:'#94a3b8'}}>{q.label_high}</span>
            </div>
        )}
    </div>
);

const StarsInput = ({ value, onChange }) => (
    <div className="d-flex gap-2">
        {[1,2,3,4,5].map(n => (
            <Star key={n} size={32}
                fill={value >= n ? '#f59e0b' : 'none'}
                color={value >= n ? '#f59e0b' : '#d1d5db'}
                style={{cursor:'pointer'}}
                onClick={() => onChange(n)}/>
        ))}
    </div>
);

const MCQInput = ({ q, value, onChange }) => (
    <div className="d-flex flex-column gap-2">
        {q.options.map((opt, i) => (
            <div key={i}
                onClick={() => onChange(opt)}
                style={{
                    padding:'10px 16px', borderRadius:8, cursor:'pointer',
                    border: value===opt ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                    background: value===opt ? '#eef2ff' : '#fff',
                    fontSize:14, transition:'all 0.15s',
                }}>
                {opt}
            </div>
        ))}
    </div>
);

const Rating3 = ({ q, value, onChange }) => (
    <div className="d-flex gap-3 flex-wrap">
        {q.options.map((opt, i) => (
            <Button key={i} size="sm"
                variant={value===opt ? 'primary' : 'outline-secondary'}
                className="rounded-pill px-4"
                onClick={() => onChange(opt)}>
                {opt}
            </Button>
        ))}
    </div>
);

// ════════════════════════════════════════════════════════════════════════════
const ExitInterview = () => {
    const { user }        = useAuth();
    const [section, setSection]   = useState(0);
    const [answers, setAnswers]   = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted,  setSubmitted]  = useState(false);
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);

    useEffect(() => {
        // Check if employee already submitted
        axiosInstance.get('/offboarding/exit-interview/my')
            .then(r => { if (r.data.data) setAlreadySubmitted(true); })
            .catch(() => {});
    }, []);

    const setAnswer = (id, value) => setAnswers(p => ({ ...p, [id]: value }));

    const currentSection  = SECTIONS[section];
    const totalSections   = SECTIONS.length;
    const progress        = Math.round(((section) / totalSections) * 100);

    const validateSection = () => {
        const required = currentSection.questions.filter(q => q.required);
        const missing  = required.filter(q => !answers[q.id] && answers[q.id] !== 0);
        if (missing.length) {
            toast.error(`Please answer all required questions (${missing.length} missing)`);
            return false;
        }
        return true;
    };

    const next = () => { if (validateSection()) setSection(s => Math.min(s+1, totalSections-1)); };
    const prev = () => setSection(s => Math.max(s-1, 0));

    const submit = async () => {
        if (!validateSection()) return;
        setSubmitting(true);
        try {
            await axiosInstance.post('/offboarding/exit-interview', {
                answers,
                employee_id: user.id,
            });
            setSubmitted(true);
            toast.success('Exit interview submitted. Thank you!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Submission failed');
        } finally { setSubmitting(false); }
    };

    if (alreadySubmitted) return (
        <Container style={{maxWidth:640}} className="py-5">
            <Card className="border-0 shadow-sm text-center p-5">
                <CheckCircle size={48} color="#059669" className="mb-3 mx-auto"/>
                <h5 className="fw-bold mb-2">Already Submitted</h5>
                <p className="text-muted">You have already completed your exit interview. Thank you for your feedback!</p>
            </Card>
        </Container>
    );

    if (submitted) return (
        <Container style={{maxWidth:640}} className="py-5">
            <Card className="border-0 shadow-sm text-center p-5">
                <div style={{fontSize:56, marginBottom:16}}>🙏</div>
                <h5 className="fw-bold mb-2">Thank you, {user?.first_name}!</h5>
                <p className="text-muted mb-0">
                    Your exit interview has been submitted. Your honest feedback helps us
                    build a better workplace for everyone. We wish you all the best in your next chapter!
                </p>
            </Card>
        </Container>
    );

    return (
        <Container style={{maxWidth:700}} className="py-4">
            {/* Header */}
            <div className="text-center mb-4">
                <div style={{fontSize:32, marginBottom:8}}>
                    <LogOut size={32} className="text-primary"/>
                </div>
                <h4 className="fw-bold mb-1">Exit Interview</h4>
                <p className="text-muted small mb-0">
                    Your honest feedback helps us improve. All responses are confidential.
                </p>
            </div>

            {/* Progress */}
            <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2 small text-muted">
                    <span>Section {section+1} of {totalSections}: <strong>{currentSection.icon} {currentSection.title}</strong></span>
                    <span>{progress}% complete</span>
                </div>
                <ProgressBar now={progress} variant="primary" style={{height:6, borderRadius:10}}/>
                {/* Section dots */}
                <div className="d-flex gap-1 mt-2 justify-content-center">
                    {SECTIONS.map((s, i) => (
                        <div key={i} style={{
                            width: i===section ? 20 : 8, height:8, borderRadius:4,
                            background: i < section ? '#059669' : i===section ? '#4f46e5' : '#e5e7eb',
                            transition:'all 0.2s',
                        }}/>
                    ))}
                </div>
            </div>

            {/* Questions */}
            <Card className="border-0 shadow-sm p-4 mb-4">
                <h6 className="fw-bold mb-4 d-flex align-items-center gap-2">
                    <span style={{fontSize:20}}>{currentSection.icon}</span>
                    {currentSection.title}
                </h6>

                {currentSection.questions.map((q, qi) => (
                    <div key={q.id} className="mb-4 pb-4"
                        style={{borderBottom: qi < currentSection.questions.length-1 ? '1px solid #f1f5f9' : 'none'}}>
                        <div className="fw-bold small mb-2 d-flex gap-2 align-items-start">
                            <span style={{
                                minWidth:22, height:22, borderRadius:6,
                                background:'#eef2ff', color:'#4f46e5',
                                display:'flex', alignItems:'center', justifyContent:'center',
                                fontSize:11, fontWeight:700, flexShrink:0, marginTop:1,
                            }}>
                                {qi+1}
                            </span>
                            <span>
                                {q.text}
                                {q.required && <span className="text-danger ms-1">*</span>}
                            </span>
                        </div>

                        {q.type === 'nps' && (
                            <NPSInput q={q} value={answers[q.id]}
                                onChange={v => setAnswer(q.id, v)}/>
                        )}
                        {q.type === 'stars' && (
                            <StarsInput value={answers[q.id] || 0}
                                onChange={v => setAnswer(q.id, v)}/>
                        )}
                        {q.type === 'mcq' && (
                            <MCQInput q={q} value={answers[q.id]}
                                onChange={v => setAnswer(q.id, v)}/>
                        )}
                        {q.type === 'boolean' && (
                            <div className="d-flex gap-3">
                                {['Yes','No'].map(opt => (
                                    <Button key={opt} size="sm"
                                        variant={answers[q.id]===opt?'primary':'outline-secondary'}
                                        className="rounded-pill px-4"
                                        onClick={() => setAnswer(q.id, opt)}>
                                        {opt}
                                    </Button>
                                ))}
                            </div>
                        )}
                        {q.type === 'rating3' && (
                            <Rating3 q={q} value={answers[q.id]}
                                onChange={v => setAnswer(q.id, v)}/>
                        )}
                        {q.type === 'textarea' && (
                            <Form.Control as="textarea" rows={3}
                                placeholder={q.placeholder || 'Your answer…'}
                                value={answers[q.id] || ''}
                                onChange={e => setAnswer(q.id, e.target.value)}
                                style={{fontSize:13}}/>
                        )}
                    </div>
                ))}
            </Card>

            {/* Navigation */}
            <div className="d-flex justify-content-between align-items-center">
                <Button variant="outline-secondary" className="rounded-pill px-4"
                    disabled={section===0} onClick={prev}>
                    <ChevronLeft size={16} className="me-1"/>Previous
                </Button>

                {section < totalSections-1 ? (
                    <Button variant="primary" className="rounded-pill px-5"
                        style={{background:'linear-gradient(135deg,#4f46e5,#7c3aed)', border:'none'}}
                        onClick={next}>
                        Next<ChevronRight size={16} className="ms-1"/>
                    </Button>
                ) : (
                    <Button variant="success" className="rounded-pill px-5 fw-bold"
                        onClick={submit} disabled={submitting}>
                        {submitting ? <Spinner size="sm"/> : '✓ Submit Exit Interview'}
                    </Button>
                )}
            </div>

            <p className="text-center text-muted small mt-3">
                <AlertCircle size={12} className="me-1"/>
                All responses are confidential and used only for company improvement.
            </p>
        </Container>
    );
};

export default ExitInterview;
